/**
 * Enhances the Shopify Forms success step in two ways:
 *   1. injects the missing login link ("Tak for din oprettelse! Log ind her."
 *      — ClickUp 869ehjy2a);
 *   2. scrolls the confirmation into view after sign-up (ClickUp 869f2r7wq).
 *
 * The success copy lives in the Forms app config, which only supports plain
 * text, so the link has to be injected client-side. <shopify-forms-embed>
 * renders with an OPEN shadow root whose [data-current-step] div flips to
 * "success" after submit (same detection as membership-signup.js). The word
 * "her" in the phrase "log ind her" becomes a link to the customer-accounts
 * login; inside the membership overlay the link returns to the cart instead,
 * matching the overlay's own footer link. If the phrase is ever edited out of
 * the Forms success message, the script leaves the message untouched (its
 * observer just stays attached, doing nothing) — but the scroll still fires,
 * which is why it carries its own one-shot flag rather than riding on the
 * linkify result.
 *
 * Login URLs are passed in by snippets/_forms-success-login-link.liquid via
 * data attributes on this script tag.
 */
(() => {
  const { loginUrl, overlayLoginUrl } = document.currentScript.dataset;
  const PHRASE = /log ind her/i;

  /**
   * @param {Element} embed - The <shopify-forms-embed> host element.
   * @returns {Element|null} The step wrapper, only while it is on the success step.
   */
  const successStep = (embed) => {
    const step = embed.shadowRoot.querySelector('[data-current-step]');
    return step && step.getAttribute('data-current-step') === 'success' ? step : null;
  };

  // One scroll per embed, ever. The observer keeps running when the login phrase
  // is absent, so the scroll cannot use linkifySuccess's return value as its guard.
  const scrolled = new WeakSet();

  /**
   * Scrolls the embed to the top of whatever scrolls it, once. The success content
   * lives in the app's shadow root and is not a target the theme can reach, so the
   * light-DOM host stands in for it — it starts at the same offset. Focus is left
   * alone here: the Forms app manages focus inside its own shadow root.
   * @param {Element} embed - The <shopify-forms-embed> host element.
   */
  const scrollToSuccess = (embed) => {
    if (scrolled.has(embed)) return;
    scrolled.add(embed);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Deferred a frame: the app swaps the tall form out for the short confirmation
    // on this same tick, and scrolling before that reflow targets a stale layout.
    requestAnimationFrame(() => {
      embed.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  };

  /**
   * Wraps the trailing "her" of the success message's "log ind her" phrase in
   * a login link. Returns true only once a link has actually been injected —
   * the app can flip the step to "success" before the message content lands,
   * so a success step without the phrase keeps being watched. After injection
   * the phrase no longer exists in a single text node ("Log ind " + <a>her</a>),
   * so re-runs can never double-link.
   * @param {Element} embed - The <shopify-forms-embed> host element.
   * @returns {boolean}
   */
  const linkifySuccess = (embed) => {
    const step = successStep(embed);
    if (!step) return false;

    const walker = document.createTreeWalker(step, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const match = node.textContent.match(PHRASE);
      if (!match) continue;
      // Split the text node so "her" (the phrase's last 3 chars) stands alone,
      // then swap that node for an anchor with the same text.
      const herNode = node.splitText(match.index + match[0].length - 3);
      herNode.splitText(3);
      const link = document.createElement('a');
      link.href = embed.closest('#membership-overlay') ? overlayLoginUrl : loginUrl;
      // "her" alone is a poor accessible name in a links list — reuse the full
      // matched phrase (whatever casing/locale the app copy carries).
      link.setAttribute('aria-label', match[0]);
      // Inside the app's shadow root the theme's stylesheets can't reach the
      // link, so the affordance has to be inline.
      link.style.textDecoration = 'underline';
      link.style.color = 'inherit';
      link.textContent = herNode.textContent;
      herNode.replaceWith(link);
      return true;
    }
    return false;
  };

  /**
   * Applies both success-step enhancements to one embed. Returns linkifySuccess's
   * verdict unchanged, so the observer below still disconnects only once a link has
   * actually been injected — the scroll guards itself.
   * @param {Element} embed - The <shopify-forms-embed> host element.
   * @returns {boolean}
   */
  const enhanceSuccess = (embed) => {
    if (!successStep(embed)) return false;
    scrollToSuccess(embed);
    return linkifySuccess(embed);
  };

  /**
   * Handles one hydrated embed: enhance now if the form was already submitted,
   * otherwise watch its shadow root until the success step appears.
   * @param {Element} embed - The <shopify-forms-embed> host element.
   */
  const watch = (embed) => {
    if (enhanceSuccess(embed)) return;
    const observer = new MutationObserver(() => {
      if (enhanceSuccess(embed)) observer.disconnect();
    });
    observer.observe(embed.shadowRoot, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-current-step']
    });
  };

  // Shopify Forms creates each embed's shadow root lazily — in the membership
  // overlay only once the overlay becomes visible, possibly minutes after page
  // load — so the poll never gives up. Each tick is a cheap no-op once every
  // embed found so far is being watched.
  const watched = new WeakSet();
  setInterval(() => {
    document.querySelectorAll('shopify-forms-embed').forEach((embed) => {
      if (watched.has(embed) || !embed.shadowRoot) return;
      watched.add(embed);
      watch(embed);
    });
  }, 500);
})();
